import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Sparkles } from "lucide-react";
import api from "../api/client";

const contentTypes = [
  { value: "blog_post", label: "Blog Post", desc: "Informative article for your blog" },
  { value: "how_to_guide", label: "How-To Guide", desc: "Step-by-step instructions" },
  { value: "comparison", label: "Comparison", desc: "Compare products, services, or approaches" },
  { value: "listicle", label: "Listicle", desc: "Numbered list format article" },
  { value: "case_study", label: "Case Study", desc: "Problem, solution, and results" },
];

export default function ContentNew({ selectedSite }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [templates, setTemplates] = useState([]);
  const [form, setForm] = useState({
    target_keyword: "",
    target_location: "",
    content_type: "blog_post",
    template: "",
    publish_mode: "review",
    schedule_date: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Pre-fill from URL params (e.g. /content/new?keyword=bathroom+renovation)
  useEffect(() => {
    const keyword = searchParams.get("keyword");
    const location = searchParams.get("location");
    const contentType = searchParams.get("type");
    if (keyword) {
      setForm((prev) => ({
        ...prev,
        target_keyword: keyword,
        ...(location ? { target_location: location } : {}),
        ...(contentType ? { content_type: contentType } : {}),
      }));
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!selectedSite) return;
    api
      .get("/content/templates/", { params: { website: selectedSite.id } })
      .then((res) => setTemplates(res.data.results || res.data))
      .catch(() => {});
  }, [selectedSite]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.target_keyword.trim()) {
      setError("Keyword is required");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await api.post(`/websites/${selectedSite.id}/content/`, {
        website: selectedSite.id,
        target_keyword: form.target_keyword.trim(),
        target_location: form.target_location.trim(),
        content_type: form.content_type,
        template: form.template || null,
        publish_mode: form.publish_mode,
        schedule_date: form.schedule_date || null,
      });
      navigate("/content");
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to create content");
    } finally {
      setSubmitting(false);
    }
  };

  if (!selectedSite) {
    return (
      <div className="text-gray-500 text-center py-20">
        Select a website first
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate("/content")}
          className="p-2 hover:bg-dark-50/50 rounded-lg transition-colors"
        >
          <ArrowLeft size={18} className="text-gray-400" />
        </button>
        <h2 className="text-lg font-semibold">Create New Content</h2>
      </div>

      <form onSubmit={handleSubmit} className="bg-dark-300 border border-dark-50 rounded-xl p-6 space-y-5">
        {/* Title / Target Keyword */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Title / Target Keyword *
          </label>
          <input
            type="text"
            value={form.target_keyword}
            onChange={(e) => setForm({ ...form, target_keyword: e.target.value })}
            placeholder='e.g. "dental office construction costs guide"'
            className="w-full bg-dark border border-dark-50 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-copper/50"
          />
        </div>

        {/* Content Type */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Content Type
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {contentTypes.map((ct) => (
              <button
                key={ct.value}
                type="button"
                onClick={() => setForm({ ...form, content_type: ct.value })}
                className={`text-left p-3 rounded-lg border transition-colors ${
                  form.content_type === ct.value
                    ? "border-copper bg-copper/10 text-copper"
                    : "border-dark-50 text-gray-400 hover:border-gray-600"
                }`}
              >
                <div className="text-xs font-medium">{ct.label}</div>
                <div className="text-[10px] mt-0.5 opacity-70">{ct.desc}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Target Location */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Target Location <span className="text-gray-500">(optional)</span>
          </label>
          <input
            type="text"
            value={form.target_location}
            onChange={(e) => setForm({ ...form, target_location: e.target.value })}
            placeholder='e.g. "Westchester, NY"'
            className="w-full bg-dark border border-dark-50 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-copper/50"
          />
        </div>

        {/* Schedule Date */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Schedule Date <span className="text-gray-500">(optional)</span>
          </label>
          <input
            type="datetime-local"
            value={form.schedule_date}
            onChange={(e) => setForm({ ...form, schedule_date: e.target.value })}
            className="w-full bg-dark border border-dark-50 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-copper/50"
          />
          <p className="text-xs text-gray-500 mt-1">
            Leave empty to generate immediately
          </p>
        </div>

        {/* Template */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Content Template
          </label>
          <select
            value={form.template}
            onChange={(e) => setForm({ ...form, template: e.target.value })}
            className="w-full bg-dark border border-dark-50 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-copper/50"
          >
            <option value="">No template (auto-generate)</option>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} ({t.website_domain || "Global"})
              </option>
            ))}
          </select>
        </div>

        {/* Publish Mode */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Publish Mode
          </label>
          <div className="grid grid-cols-3 gap-3">
            {[
              { value: "review", label: "Queue for Review", desc: "Review before publishing" },
              { value: "auto", label: "Auto-Publish", desc: "Publish to WordPress automatically" },
              { value: "html", label: "Save as HTML", desc: "Download HTML output" },
            ].map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setForm({ ...form, publish_mode: opt.value })}
                className={`text-left p-3 rounded-lg border transition-colors ${
                  form.publish_mode === opt.value
                    ? "border-copper bg-copper/10 text-copper"
                    : "border-dark-50 text-gray-400 hover:border-gray-600"
                }`}
              >
                <div className="text-sm font-medium">{opt.label}</div>
                <div className="text-xs mt-0.5 opacity-70">{opt.desc}</div>
              </button>
            ))}
          </div>
        </div>

        {error && (
          <div className="text-red-400 text-sm bg-red-900/20 border border-red-800/30 rounded-lg px-4 py-2">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-copper hover:bg-copper-600 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
        >
          <Sparkles size={16} />
          {submitting ? "Starting Pipeline..." : "Generate Content"}
        </button>

        <p className="text-xs text-gray-500 text-center">
          Pipeline: SERP analysis (if available) → blueprint → AI content generation → quality gate → review
        </p>
      </form>
    </div>
  );
}
